import { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Button, SectionHeading, EmptyState } from '@ets/design-system';

import {
  useDeleteConnectionMutation,
  useListConnectionsQuery,
  useListServicesQuery,
} from '../UserSettingsApi';

import { Table, Cell, type TableColumn, type TableRow } from '_common/components/Table';
import IntlErrorBoundary from '_common/components/IntlErrorBoundary/IntlErrorBoundary';

import styles from './Authentication.module.scss';

type RowData = ConnectionLink & { id: string };

const Authentication = () => {
  const intl = useIntl();

  const { data: providerList } = useListServicesQuery();
  const createAndDelete = providerList?.createAndDelete || false;
  const totalProviders = providerList?.providers.length || 0;
  const { data: connections } = useListConnectionsQuery();
  const [deleteConnection] = useDeleteConnectionMutation();

  const columns = useMemo<TableColumn<RowData>[]>(() => {
    const columns: TableColumn<RowData>[] = [
      {
        id: 'id',
        accessorKey: 'uuid',
        enableSorting: false,
        header: intl.formatMessage({ id: 'settings.authentication.id' }),
        cell: (info) => {
          const connection = info.row.original;
          return <Cell testId={`${connection.uuid}-uuid-column`}>{connection.uuid}</Cell>;
        },
      },
      {
        id: 'service',
        accessorKey: 'provider',
        enableSorting: false,
        header: intl.formatMessage({ id: 'settings.authentication.service' }),
        size: 120,
        cell: (info) => {
          const connection = info.row.original;
          return <Cell testId={`${connection.uuid}-service-column`}>{connection.provider}</Cell>;
        },
      },
      {
        id: 'creation',
        accessorKey: 'creation',
        enableSorting: false,
        header: intl.formatMessage({ id: 'global.creationDate' }),
        size: 160,
        cell: (info) => {
          const connection = info.row.original;

          return (
            <Cell testId={`${connection.uuid}-creation-column`}>
              <Cell.DateTime
                date={{ date: connection.creation }}
                time={{ time: connection.creation }}
              />
            </Cell>
          );
        },
      },
    ];

    if (createAndDelete) {
      columns.push({
        id: 'remove',
        size: 90,
        enableSorting: false,
        cell: (info) => {
          const connection = info.row.original;

          return (
            <Cell testId={`${connection.uuid}-remove-column`}>
              <Button
                size="s"
                variant="link"
                onClick={() => {
                  deleteConnection(connection.pk);
                }}
                icon="trash"
                testId={`${connection.uuid}-remove`}
              >
                <FormattedMessage id="global.remove" />
              </Button>
            </Cell>
          );
        },
      });
    }

    return columns;
  }, [createAndDelete]);

  const rowsData = useMemo(() => {
    if (!connections) {
      return [];
    }
    return connections.map<TableRow<RowData>>((connection) => {
      return {
        id: connection.uuid,
        ...connection,
      };
    });
  }, [connections, createAndDelete]);

  const renderNoAuthenticators = () => {
    return (
      <div className={styles.noAuthenticators}>
        <EmptyState
          size="m"
          title={intl.formatMessage({ id: 'settings.authentication.paragraph1' })}
          description={<FormattedMessage id="settings.authentication.paragraph2" />}
          testId="no-authenticators"
        />
      </div>
    );
  };

  const renderEmptyMessage = () => {
    return (
      <div className={styles.emptyMessage}>
        <FormattedMessage id="settings.authentication.empty" />
      </div>
    );
  };

  if (totalProviders === 0) {
    return renderNoAuthenticators();
  }

  return (
    <>
      <div className={styles.container}>
        <IntlErrorBoundary fallbackType="sectionHeading" mockProps={{ size: 'l', sticky: true }}>
          <SectionHeading
            size="l"
            sticky
            title={intl.formatMessage({ id: 'settings.authentication.header' })}
            testId="active-connections"
          />
        </IntlErrorBoundary>
        {connections?.length === 0 ? (
          renderEmptyMessage()
        ) : (
          <IntlErrorBoundary size="xl" style={{ margin: '10rem 0 0 0' }}>
            <div className={styles.root}>
              <Table
                identity="userPreferences/authentication"
                options={{ columns, data: rowsData }}
                renderFooter={() => null}
                isLoading={false}
                loadingLabel={intl.formatMessage({ id: 'LOADING_ELEMENTS' })}
                testId="userPreferences_authentication"
                selectable={false}
              />
            </div>
          </IntlErrorBoundary>
        )}
      </div>
    </>
  );
};

export default Authentication;
